import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

@Component({
  selector: 'app-count-up',
  template: `<div class="count-up">{{ display() }}</div>`,
  styleUrl: './count-up.css',
})
export class CountUpComponent implements OnInit {
  readonly raw = input.required<string>();
  readonly delay = input(0);
  readonly durationMs = input(1800);

  private readonly destroyRef = inject(DestroyRef);

  private readonly NUMBER_PATTERN = /^([\d.,]+)(.*)$/;
  private readonly LARGE_VALUE_THRESHOLD = 1900;
  private readonly LARGE_VALUE_MAX_OFFSET = 60;
  private readonly LARGE_VALUE_OFFSET_RATIO = 0.04;
  private readonly MEDIUM_VALUE_THRESHOLD = 100;
  private readonly MEDIUM_VALUE_START_RATIO = 0.3;
  private readonly EASE_EXPONENT = 3;

  private readonly value = signal<number | null>(null);

  protected readonly display = computed(() => {
    const current = this.value();
    if (current === null) return this.raw();
    const match = this.NUMBER_PATTERN.exec(String(this.raw()));
    const isInt = match && Number.isInteger(parseFloat(match[1].replace(/,/g, '.')));
    const suffix = match ? match[2] : '';
    return (isInt ? Math.round(current).toString() : current.toFixed(1)) + suffix;
  });

  ngOnInit() {
    const match = this.NUMBER_PATTERN.exec(String(this.raw()));
    if (!match) return;
    const target = parseFloat(match[1].replace(/,/g, '.'));
    if (!Number.isFinite(target)) return;

    const start = this.computeStart(target);
    this.value.set(start);

    let frame = 0;
    let startTime = 0;
    const ease = (t: number) => 1 - Math.pow(1 - t, this.EASE_EXPONENT);
    const delay = this.delay();
    const duration = this.durationMs();

    const tick = (now: number) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime - delay;
      if (elapsed < 0) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(1, elapsed / duration);
      this.value.set(start + (target - start) * ease(progress));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    this.destroyRef.onDestroy(() => cancelAnimationFrame(frame));
  }

  private computeStart(target: number): number {
    if (target >= this.LARGE_VALUE_THRESHOLD) {
      return Math.max(
        target - Math.min(this.LARGE_VALUE_MAX_OFFSET, Math.floor(target * this.LARGE_VALUE_OFFSET_RATIO)),
        this.LARGE_VALUE_THRESHOLD,
      );
    }
    if (target >= this.MEDIUM_VALUE_THRESHOLD) {
      return Math.floor(target * this.MEDIUM_VALUE_START_RATIO);
    }
    return 0;
  }
}
